import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FileText, Briefcase, Globe, PhoneCall, CheckCircle, ArrowRight } from 'lucide-react';

function Services() {
  const { t } = useTranslation();

  const topCards = [
    {
      icon: <FileText size={24} color="#4792f4" />,
      iconBg: 'rgba(71,146,244,0.15)',
      title: t('svc1Title'),
      items: [t('svc1Item1'), t('svc1Item2'), t('svc1Item3')],
      numbered: false,
      style: 'classic',
      anim: { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } } },
    },
    {
      icon: <Briefcase size={24} color="#fff" />,
      iconBg: 'rgba(71,146,244,0.3)',
      title: t('svc2Title'),
      items: [t('svc2Item1'), t('svc2Item2'), t('svc2Item3')],
      numbered: true,
      style: 'dark',
      anim: { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut', delay: 0.1 } } },
    },
    {
      icon: <Globe size={24} color="#10b981" />,
      iconBg: 'rgba(16,185,129,0.18)',
      title: t('svc3Title'),
      items: [t('svc3Item1'), t('svc3Item2'), t('svc3Item3')],
      numbered: false,
      style: 'outline',
      anim: { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut', delay: 0.2 } } },
    },
  ];

  const ctaCard = {
    icon: <PhoneCall size={26} color="#ffffff" />,
    title: t('svc4Title'),
    desc: t('svc4Desc'),
    anim: { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut', delay: 0.3 } } },
  };

  return (
    <section id="services">
      <style>{`
        .svc-section {
          background: linear-gradient(135deg, #0f2350 0%, #1b3f82 50%, #2563eb 100%);
          border-top: 1px solid rgba(71,146,244,0.30);
          padding: 5rem 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2.5rem;
        }
        .svc-header { text-align: center; }
        .svc-badge {
          display: inline-flex; align-items: center; gap: 0.5rem;
          background: rgba(71,146,244,0.25);
          border: 1px solid rgba(168,200,255,0.45);
          border-radius: 50px; padding: 0.35rem 1rem;
          font-size: 0.78rem; font-weight: 700; color: #a8c8ff;
          letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 1rem;
        }
        .svc-title {
          font-size: clamp(1.8rem, 3vw, 2.6rem); font-weight: 700;
          color: #ffffff; margin: 0 0 0.5rem; letter-spacing: -0.02em;
        }
        .svc-subtitle { font-size: 1rem; color: #c0d8ff; margin: 0; }

        .svc-body {
          max-width: 1100px; width: 100%;
          display: flex; flex-direction: column; gap: 1.25rem;
        }

        /* Top 3-column row */
        .svc-top-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.25rem;
          align-items: stretch;
        }

        /* ── Card 1: Classic white ── */
        .svc-card-classic {
          background: #f8fbff;
          border: 1px solid rgba(71,146,244,0.14);
          border-top: 3px solid #4792f4;
          border-radius: 16px;
          padding: 1.6rem;
          display: flex; flex-direction: column; gap: 1.1rem;
          transition: box-shadow 0.25s, transform 0.25s;
        }
        .svc-card-classic:hover {
          box-shadow: 0 10px 32px rgba(71,146,244,0.18);
          transform: translateY(-3px);
        }

        /* ── Card 2: Dark glass ── */
        .svc-card-dark {
          background: rgba(10,20,50,0.88);
          border: 1px solid rgba(71,146,244,0.25);
          border-top: 3px solid #4792f4;
          border-radius: 16px;
          padding: 1.6rem;
          display: flex; flex-direction: column; gap: 1.1rem;
          backdrop-filter: blur(8px);
          transition: box-shadow 0.25s, transform 0.25s;
        }
        .svc-card-dark:hover {
          border-color: rgba(71,146,244,0.5);
          box-shadow: 0 10px 32px rgba(71,146,244,0.2);
          transform: translateY(-3px);
        }
        .svc-card-dark .svc-card-title { color: #fff; }
        .svc-card-dark .svc-list li { color: #c0d8ff; }
        .svc-card-dark .svc-bullet { background: rgba(71,146,244,0.25); color: #7cb8ff; }

        /* ── Card 3: Outlined / green ── */
        .svc-card-outline {
          background: rgba(16,185,129,0.05);
          border: 1px solid rgba(16,185,129,0.35);
          border-top: 3px solid #10b981;
          border-radius: 16px;
          padding: 1.6rem;
          display: flex; flex-direction: column; gap: 1.1rem;
          transition: box-shadow 0.25s, transform 0.25s;
        }
        .svc-card-outline:hover {
          border-color: rgba(16,185,129,0.65);
          box-shadow: 0 10px 32px rgba(16,185,129,0.14);
          transform: translateY(-3px);
        }
        .svc-card-outline .svc-card-title { color: #e0fff5; }
        .svc-card-outline .svc-list li { color: #a8f0d8; }
        .svc-card-outline .svc-bullet-tag {
          display: inline-flex; align-items: center; gap: 0.35rem;
          background: rgba(16,185,129,0.15);
          border: 1px solid rgba(16,185,129,0.3);
          border-radius: 20px; padding: 0.25rem 0.75rem;
          font-size: 0.82rem; color: #6ee7b7;
        }

        /* ── CTA card (full width) ── */
        .svc-card-cta {
          background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 40%, #4792f4 100%);
          border-radius: 16px;
          padding: 2rem 2.5rem;
          display: flex;
          align-items: center;
          gap: 2rem;
          box-shadow: 0 8px 32px rgba(37,99,235,0.4);
          transition: box-shadow 0.25s, transform 0.25s;
        }
        .svc-card-cta:hover {
          box-shadow: 0 14px 44px rgba(37,99,235,0.5);
          transform: translateY(-2px);
        }
        .svc-cta-icon-wrap {
          width: 56px; height: 56px; border-radius: 14px;
          background: rgba(255,255,255,0.18);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .svc-cta-content { flex: 1; }
        .svc-cta-title {
          font-size: 1.15rem; font-weight: 700; color: #fff;
          margin: 0 0 0.5rem;
        }
        .svc-cta-desc {
          font-size: 0.92rem; color: rgba(255,255,255,0.88);
          line-height: 1.7; margin: 0;
        }
        .svc-cta-arrow {
          width: 44px; height: 44px; border-radius: 50%;
          background: rgba(255,255,255,0.18);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          transition: background 0.2s, transform 0.2s;
        }
        .svc-card-cta:hover .svc-cta-arrow {
          background: rgba(255,255,255,0.28);
          transform: translateX(4px);
        }

        /* Shared */
        .svc-icon-wrap {
          width: 46px; height: 46px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .svc-card-title {
          font-size: 0.97rem; font-weight: 700;
          color: #0f1f40; margin: 0; line-height: 1.4;
        }
        .svc-list {
          list-style: none; padding: 0; margin: 0;
          display: flex; flex-direction: column; gap: 0.55rem;
        }
        .svc-list li {
          display: flex; align-items: flex-start; gap: 0.6rem;
          font-size: 0.9rem; color: #1a3260; line-height: 1.5;
        }
        .svc-bullet {
          width: 22px; height: 22px; border-radius: 50%;
          background: rgba(71,146,244,0.12);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; margin-top: 1px;
          font-size: 0.72rem; font-weight: 700; color: #4792f4;
        }
        .svc-tags {
          display: flex; flex-wrap: wrap; gap: 0.45rem;
        }

        @media (max-width: 860px) {
          .svc-top-row { grid-template-columns: 1fr; }
          .svc-card-cta { flex-direction: column; align-items: flex-start; gap: 1.25rem; }
          .svc-cta-arrow { display: none; }
        }
      `}</style>

      <div className="svc-section">
        <motion.div
          className="svc-header"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="svc-badge">SERVICES</div>
          <h2 className="svc-title">{t('servicesTitle')}</h2>
          <p className="svc-subtitle">{t('servicesSubtitle')}</p>
        </motion.div>

        <motion.div
          className="svc-body"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {/* Top 3 cards */}
          <div className="svc-top-row">
            {topCards.map((card, i) => (
              <motion.div
                key={i}
                className={`svc-card-${card.style}`}
                variants={card.anim}
                whileHover={{ scale: 1.015, transition: { duration: 0.2 } }}
              >
                <motion.div
                  className="svc-icon-wrap"
                  style={{ background: card.iconBg }}
                  whileHover={{ rotate: [0, -8, 8, 0], transition: { duration: 0.4 } }}
                >
                  {card.icon}
                </motion.div>

                <p className="svc-card-title">{card.title}</p>

                {card.style !== 'outline' ? (
                  <ul className="svc-list">
                    {card.items.map((item, j) => (
                      <motion.li
                        key={j}
                        initial={{ opacity: 0, x: -8 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: j * 0.08 }}
                      >
                        <span className="svc-bullet">
                          {card.numbered ? j + 1 : <CheckCircle size={13} color="#4792f4" />}
                        </span>
                        {item}
                      </motion.li>
                    ))}
                  </ul>
                ) : (
                  <div className="svc-tags">
                    {card.items.map((item, j) => (
                      <motion.span
                        className="svc-bullet-tag"
                        key={j}
                        initial={{ opacity: 0, scale: 0.85 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: j * 0.1 }}
                      >
                        <CheckCircle size={12} color="#10b981" />
                        {item}
                      </motion.span>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Full-width CTA */}
          <motion.div
            className="svc-card-cta"
            variants={ctaCard.anim}
            whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
          >
            <motion.div
              className="svc-cta-icon-wrap"
              whileHover={{ rotate: [0, -10, 10, 0], transition: { duration: 0.4 } }}
            >
              {ctaCard.icon}
            </motion.div>
            <div className="svc-cta-content">
              <p className="svc-cta-title">{ctaCard.title}</p>
              <p className="svc-cta-desc">{ctaCard.desc}</p>
            </div>
            <div className="svc-cta-arrow">
              <ArrowRight size={20} color="#fff" />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default Services;
